import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaSearch, FaUserCircle } from 'react-icons/fa';
import CustomButton from '../CustomButton';
import { getAutoReplies, saveAutoReply } from '../../Services/User/Chat';
import { getChatList } from '../../Services/User/Chat';
import { showToast } from '../Toast/Toast';
import useUserStore from '../../Stores/UserStore';
import './AutoReplyModal.css';

const AutoReplyModal = ({ show, onHide }) => {
  const { user } = useUserStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [autoReplies, setAutoReplies] = useState([]);
  const [selectedAutoReply, setSelectedAutoReply] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  // Default auto reply options
  const defaultAutoReplies = [
    'Hi! Thanks for accepting my request.',
    'Hello ! i am offline talk to you later',
    'Hello ! lets talk laater i am busy right now'
  ];

  useEffect(() => {
    if (show) {
      fetchAutoReplies();
    }
  }, [show]);

  // Debounced search effect
  useEffect(() => {
    if (!show) return;
    
    const delayDebounce = setTimeout(() => {
      fetchChatList();
    }, 300);

    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, show]);

  const fetchChatList = async () => {
    try {
      setLoading(true);
      const response = await getChatList(searchQuery);
      const chatListData = Array.isArray(response.data) ? response.data : [];
      setChatList(chatListData);
    } catch (error) {
      console.error('Error fetching chat list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoReplies = async () => {
    try {
      const response = await getAutoReplies();
      if (response.data) {
        // If API returns auto replies, use them, otherwise use defaults
        const replies = Array.isArray(response.data) ? response.data : defaultAutoReplies;
        setAutoReplies(replies);
      } else {
        setAutoReplies(defaultAutoReplies);
      }
    } catch (error) {
      console.error('Error fetching auto replies:', error);
      // Use default replies on error
      setAutoReplies(defaultAutoReplies);
    }
  };

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    setSelectedAutoReply('');
    setCustomMessage('');
  };

  const handleAutoReplySelect = (reply) => {
    setSelectedAutoReply(reply);
    setCustomMessage('');
  };

  const handleCustomMessageChange = (e) => {
    setCustomMessage(e.target.value);
    setSelectedAutoReply('');
  };

  const handleClose = () => {
    setSelectedChat(null);
    setCustomMessage('');
    setSelectedAutoReply('');
    setSearchQuery('');
    onHide();
  };

  const handleSave = async () => {
    if (!selectedChat) {
      showToast('Please select a user', 'error');
      return;
    }

    const messageToSave = customMessage.trim() || selectedAutoReply;

    if (!messageToSave) {
      showToast('Please enter a message or select an auto reply', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await saveAutoReply(messageToSave);
      
      if (response.status) {
        showToast('Auto reply saved successfully!', 'success');
        handleClose();
      } else {
        showToast(response.message || 'Failed to save auto reply', 'error');
      }
    } catch (error) {
      console.error('Error saving auto reply:', error);
      showToast(error.message || 'Failed to save auto reply', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderAvatar = (src, alt, className, imageId) => {
    if (!src || src === 'https://via.placeholder.com/50') {
      return <FaUserCircle className={`${className} avatar-icon`} />;
    }
    
    return (
      <img 
        src={src} 
        alt={alt}
        className={className}
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling?.classList.add('avatar-icon');
        }}
      />
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered className="auto-reply-modal" size="xl">
      <Modal.Body className="auto-reply-modal-body">
        <button className="modal-close-btn" onClick={handleClose}>
          <FaTimes />
        </button>

        <div className="auto-reply-container">
          {/* Left Column - User List */}
          <div className="auto-reply-left">
            <div className="auto-reply-search">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="auto-reply-user-list">
              {loading && chatList.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Loading...</p>
                </div>
              ) : chatList.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">No users found</p>
                </div>
              ) : (
                chatList.map((chat) => (
                  <div
                    key={chat.id}
                    className={`auto-reply-user-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                    onClick={() => handleChatSelect(chat)}
                  >
                    <div className="user-avatar">
                      {renderAvatar(
                        chat.image ? `${imageBaseUrl}${chat.image}` : null,
                        chat.name || 'User',
                        'user-avatar-img',
                        `auto-reply-${chat.id}`
                      )}
                      {chat.unread > 0 && (
                        <span className="unread-badge">{chat.unread}</span>
                      )}
                    </div>
                    <div className="user-info">
                      <h6 className="user-name">{chat.name || `Chat #${chat.id}`}</h6>
                      <p className="user-description">lorem ipsum sit amet</p>
                    </div>
                    <div className="user-meta">
                      <span className="user-time">1 Days Ago</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right Column - Reply Options */}
          <div className="auto-reply-right">
            <div className="auto-reply-content">
              {/* Create Your Own Reply */}
              <div className="reply-section">
                <h5 className="reply-section-title">Create Your Own Reply:</h5>
                <textarea
                  className="custom-reply-textarea"
                  placeholder="Write here"
                  value={customMessage}
                  onChange={handleCustomMessageChange}
                  rows={6}
                />
              </div>

              {/* Choose Auto Reply */}
              <div className="reply-section">
                <h5 className="reply-section-title">Choose Auto Reply:</h5>
                <div className="auto-reply-options">
                  {autoReplies.map((reply, index) => (
                    <div key={index} className="auto-reply-option">
                      <input
                        type="radio"
                        id={`auto-reply-${index}`}
                        name="auto-reply"
                        value={reply}
                        checked={selectedAutoReply === reply}
                        onChange={() => handleAutoReplySelect(reply)}
                        className="auto-reply-radio"
                      />
                      <label htmlFor={`auto-reply-${index}`} className="auto-reply-label">
                        <div className="auto-reply-message-box">
                          {reply}
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <div className="auto-reply-actions">
                <CustomButton
                  onClick={handleSave}
                  disabled={loading || !selectedChat}
                  className="save-auto-reply-btn"
                >
                  {loading ? 'Saving...' : 'Save'}
                </CustomButton>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AutoReplyModal;

